import { NotificationType } from '@/lib/types/notifications';
import { userNotificationPreferencesService } from './userNotificationPreferencesService';

export enum SoundType {
  // Distinct notification sounds
  Default = 'default',
  Chime = 'chime',
  Bell = 'bell',
  Ding = 'ding',
  Pop = 'pop',
  Bubble = 'bubble',
  Coin = 'coin',
  Guitar = 'guitar',
  Piano = 'piano',
  Drums = 'drums',
  Whistle = 'whistle',
  Marimba = 'marimba',
  Harp = 'harp',
  
  // System sounds
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}

interface SoundConfig {
  generator: (audioContext: AudioContext, volume: number) => Promise<void>;
  description: string;
}

export class SoundNotificationService {
  private static instance: SoundNotificationService;
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;
  private volume: number = 0.3;

  // Sound generators for different notification types
  private soundConfigs: Record<SoundType, SoundConfig> = {
    [SoundType.Default]: {
      generator: this.generateDefaultSound.bind(this),
      description: 'Classic notification tone'
    },
    [SoundType.Chime]: {
      generator: this.generateChimeSound.bind(this),
      description: 'Gentle wind chime melody'
    },
    [SoundType.Bell]: {
      generator: this.generateBellSound.bind(this),
      description: 'Church bell with harmonics'
    },
    [SoundType.Ding]: {
      generator: this.generateDingSound.bind(this),
      description: 'Quick metallic ding'
    },
    [SoundType.Pop]: {
      generator: this.generatePopSound.bind(this),
      description: 'Bubble pop effect'
    },
    [SoundType.Bubble]: {
      generator: this.generateBubbleSound.bind(this),
      description: 'Water bubble rising'
    },
    [SoundType.Coin]: {
      generator: this.generateCoinSound.bind(this),
      description: 'Coin drop with bounce'
    },
    [SoundType.Guitar]: {
      generator: this.generateGuitarSound.bind(this),
      description: 'Guitar string pluck'
    },
    [SoundType.Piano]: {
      generator: this.generatePianoSound.bind(this),
      description: 'Piano key press'
    },
    [SoundType.Drums]: {
      generator: this.generateDrumSound.bind(this),
      description: 'Snare drum hit'
    },
    [SoundType.Whistle]: {
      generator: this.generateWhistleSound.bind(this),
      description: 'Sharp whistle blow'
    },
    [SoundType.Marimba]: {
      generator: this.generateMarimbaSound.bind(this),
      description: 'Warm wooden mallet roll'
    },
    [SoundType.Harp]: {
      generator: this.generateHarpSound.bind(this),
      description: 'Ascending harp arpeggio'
    },
    [SoundType.Success]: {
      generator: this.generateSuccessSound.bind(this),
      description: 'Success chord progression'
    },
    [SoundType.Warning]: {
      generator: this.generateWarningSound.bind(this),
      description: 'Alert warning tone'
    },
    [SoundType.Error]: {
      generator: this.generateErrorSound.bind(this),
      description: 'Error buzz sound'
    }
  };

  public static getInstance(): SoundNotificationService {
    if (!SoundNotificationService.instance) {
      SoundNotificationService.instance = new SoundNotificationService();
    }
    return SoundNotificationService.instance;
  }

  private constructor() {
    // Initialize audio context on first user interaction
    this.initializeAudioContext();
    this.loadSettings();
  }

  private initializeAudioContext(): void {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Resume audio context on user interaction if it's suspended
      if (this.audioContext.state === 'suspended') {
        const resumeAudio = () => {
          if (this.audioContext && this.audioContext.state === 'suspended') {
            this.audioContext.resume();
          }
          // Remove listeners after first interaction
          document.removeEventListener('click', resumeAudio);
          document.removeEventListener('keydown', resumeAudio);
          document.removeEventListener('touchstart', resumeAudio);
        };

        document.addEventListener('click', resumeAudio);
        document.addEventListener('keydown', resumeAudio);
        document.addEventListener('touchstart', resumeAudio);
      }
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
      this.audioContext = null;
    }
  }

  private loadSettings(): void {
    const savedEnabled = localStorage.getItem('soundNotifications_enabled');
    const savedVolume = localStorage.getItem('soundNotifications_volume');

    this.isEnabled = savedEnabled !== null ? savedEnabled === 'true' : true;
    this.volume = savedVolume !== null ? parseFloat(savedVolume) : 0.3;
  }

  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('soundNotifications_enabled', enabled.toString());
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume)); // Clamp between 0 and 1
    localStorage.setItem('soundNotifications_volume', this.volume.toString());
  }

  public isAudioEnabled(): boolean {
    return this.isEnabled && this.audioContext !== null;
  }

  public getVolume(): number {
    return this.volume;
  }

  // Play a sound based on notification type (with user preferences check)
  public async playNotificationSound(notificationType?: NotificationType, userId?: string): Promise<void> {
    if (!this.isAudioEnabled()) return;

    // Check if user has notifications enabled (simple localStorage check)
    if (userId) {
      const userSettings = localStorage.getItem(`userNotificationSettings_${userId}`);
      if (userSettings) {
        const settings = JSON.parse(userSettings);
        if (!settings.notificationsEnabled) {
          console.log(`Skipping sound notification for user ${userId} - notifications disabled`);
          return;
        }
        
        // Use user's selected sound instead of mapping from notification type
        await this.playSound(settings.selectedSound || SoundType.Default);
        return;
      }
    }

    // Fallback to default sound mapping if no user settings
    const soundType = this.mapNotificationTypeToSound(notificationType);
    await this.playSound(soundType);
  }

  // Play a specific sound type
  public async playSound(soundType: SoundType): Promise<void> {
    if (!this.isAudioEnabled() || !this.audioContext) return;

    try {
      const config = this.soundConfigs[soundType];
      if (config?.generator) {
        await config.generator(this.audioContext, this.volume);
      }
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  }

  // Play multiple sounds in sequence
  public async playSequence(sounds: SoundType[], delay: number = 100): Promise<void> {
    if (!this.isAudioEnabled()) return;

    for (let i = 0; i < sounds.length; i++) {
      await this.playSound(sounds[i]);
      if (i < sounds.length - 1) {
        await this.delay(delay);
      }
    }
  }

  // Play a custom chord (multiple frequencies at once)
  public async playChord(frequencies: number[], duration: number = 0.3): Promise<void> {
    if (!this.isAudioEnabled() || !this.audioContext) return;

    try {
      const oscillators: OscillatorNode[] = [];
      const gainNodes: GainNode[] = [];

      // Create oscillators for each frequency
      frequencies.forEach(frequency => {
        const oscillator = this.audioContext!.createOscillator();
        const gainNode = this.audioContext!.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext!.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(this.volume / frequencies.length, this.audioContext!.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + duration);

        oscillators.push(oscillator);
        gainNodes.push(gainNode);
      });

      // Start all oscillators
      const startTime = this.audioContext.currentTime;
      oscillators.forEach(oscillator => {
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      });
    } catch (error) {
      console.warn('Failed to play chord:', error);
    }
  }

  // Sound Generator Methods - Each creates a unique, recognizable sound

  private async generateDefaultSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Classic notification: two-tone ascending
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    const gain2 = audioContext.createGain();

    osc1.connect(gain1);
    osc2.connect(gain2);
    gain1.connect(audioContext.destination);
    gain2.connect(audioContext.destination);

    osc1.frequency.value = 800;
    osc2.frequency.value = 1000;
    osc1.type = 'sine';
    osc2.type = 'sine';

    gain1.gain.setValueAtTime(volume * 0.5, audioContext.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    
    gain2.gain.setValueAtTime(0, audioContext.currentTime + 0.1);
    gain2.gain.setValueAtTime(volume * 0.5, audioContext.currentTime + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    osc1.start(audioContext.currentTime);
    osc1.stop(audioContext.currentTime + 0.15);
    osc2.start(audioContext.currentTime + 0.1);
    osc2.stop(audioContext.currentTime + 0.3);
  }

  private async generateChimeSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Wind chime: multiple harmonious tones
    const frequencies = [523.25, 659.25, 783.99, 1046.5]; // C major chord
    const delays = [0, 0.1, 0.2, 0.3];
    
    frequencies.forEach((freq, i) => {
      setTimeout(() => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        
        osc.connect(gain);
        gain.connect(audioContext.destination);
        
        osc.frequency.value = freq;
        osc.type = 'sine';
        
        gain.gain.setValueAtTime(volume * 0.3, audioContext.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.0);
        
        osc.start(audioContext.currentTime);
        osc.stop(audioContext.currentTime + 1.0);
      }, delays[i] * 1000);
    });
  }

  private async generateBellSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Church bell with harmonics
    const fundamental = 400;
    const harmonics = [1, 2, 3, 4, 5];
    const amplitudes = [1, 0.5, 0.25, 0.125, 0.0625];
    
    harmonics.forEach((harmonic, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      
      osc.connect(gain);
      gain.connect(audioContext.destination);
      
      osc.frequency.value = fundamental * harmonic;
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(volume * amplitudes[i] * 0.3, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2.0);
      
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 2.0);
    });
  }

  private async generateDingSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Metallic ding with quick decay
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    
    osc.connect(gain);
    gain.connect(audioContext.destination);
    
    osc.frequency.value = 2000;
    osc.type = 'triangle';
    
    gain.gain.setValueAtTime(volume * 0.8, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
    
    osc.start(audioContext.currentTime);
    osc.stop(audioContext.currentTime + 0.1);
  }

  private async generatePopSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Bubble pop: quick frequency sweep down
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    
    osc.connect(gain);
    gain.connect(audioContext.destination);
    
    osc.frequency.setValueAtTime(1500, audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.05);
    osc.type = 'square';
    
    gain.gain.setValueAtTime(volume * 0.6, audioContext.currentTime);
    gain.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.05);
    
    osc.start(audioContext.currentTime);
    osc.stop(audioContext.currentTime + 0.05);
  }

  private async generateBubbleSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Rising bubble: frequency sweep up with vibrato
    const osc = audioContext.createOscillator();
    const lfo = audioContext.createOscillator();
    const gain = audioContext.createGain();
    const lfoGain = audioContext.createGain();
    
    osc.connect(gain);
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    gain.connect(audioContext.destination);
    
    osc.frequency.setValueAtTime(200, audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.4);
    osc.type = 'sine';
    
    lfo.frequency.value = 5; // 5Hz vibrato
    lfoGain.gain.value = 10; // Vibrato depth
    
    gain.gain.setValueAtTime(volume * 0.4, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
    
    osc.start(audioContext.currentTime);
    lfo.start(audioContext.currentTime);
    osc.stop(audioContext.currentTime + 0.4);
    lfo.stop(audioContext.currentTime + 0.4);
  }

  private async generateCoinSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Coin drop: metallic hit followed by bounces
    const frequencies = [1200, 1000, 800, 600];
    const delays = [0, 0.05, 0.08, 0.1];
    const volumes = [1, 0.7, 0.5, 0.3];
    
    frequencies.forEach((freq, i) => {
      setTimeout(() => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        
        osc.connect(gain);
        gain.connect(audioContext.destination);
        
        osc.frequency.value = freq;
        osc.type = 'triangle';
        
        gain.gain.setValueAtTime(volume * volumes[i] * 0.5, audioContext.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        
        osc.start(audioContext.currentTime);
        osc.stop(audioContext.currentTime + 0.1);
      }, delays[i] * 1000);
    });
  }

  private async generateGuitarSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Guitar pluck: fundamental with harmonics and quick attack
    const fundamental = 330; // E note
    const harmonics = [1, 2, 3, 4];
    const amplitudes = [1, 0.3, 0.15, 0.05];
    
    harmonics.forEach((harmonic, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      
      osc.connect(gain);
      gain.connect(audioContext.destination);
      
      osc.frequency.value = fundamental * harmonic;
      osc.type = 'sawtooth';
      
      gain.gain.setValueAtTime(volume * amplitudes[i] * 0.6, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
      
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 0.8);
    });
  }

  private async generatePianoSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Piano: Clean sine wave with harmonics
    const fundamental = 523.25; // C5
    const harmonics = [1, 2, 3];
    const amplitudes = [1, 0.4, 0.2];
    
    harmonics.forEach((harmonic, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      
      osc.connect(gain);
      gain.connect(audioContext.destination);
      
      osc.frequency.value = fundamental * harmonic;
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(volume * amplitudes[i] * 0.5, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.2);
      
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 1.2);
    });
  }

  private async generateDrumSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Snare drum: noise burst with pitch envelope
    const bufferSize = audioContext.sampleRate * 0.1;
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const output = buffer.getChannelData(0);
    
    // Generate white noise
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    
    const noise = audioContext.createBufferSource();
    const filter = audioContext.createBiquadFilter();
    const gain = audioContext.createGain();
    
    noise.buffer = buffer;
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(audioContext.destination);
    
    filter.type = 'highpass';
    filter.frequency.value = 1000;
    
    gain.gain.setValueAtTime(volume * 0.8, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
    
    noise.start(audioContext.currentTime);
    noise.stop(audioContext.currentTime + 0.1);
  }

  private async generateWhistleSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Sharp whistle: high frequency with slight vibrato
    const osc = audioContext.createOscillator();
    const lfo = audioContext.createOscillator();
    const gain = audioContext.createGain();
    const lfoGain = audioContext.createGain();
    
    osc.connect(gain);
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    gain.connect(audioContext.destination);
    
    osc.frequency.value = 3000;
    osc.type = 'sine';
    
    lfo.frequency.value = 6; // 6Hz vibrato
    lfoGain.gain.value = 20; // Vibrato depth
    
    gain.gain.setValueAtTime(volume * 0.6, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    osc.start(audioContext.currentTime);
    lfo.start(audioContext.currentTime);
    osc.stop(audioContext.currentTime + 0.3);
    lfo.stop(audioContext.currentTime + 0.3);
  }

  private async generateMarimbaSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Marimba: warm wooden mallet — short two-note motif on top of a stretched harmonic
    // (fundamental + 4th + ~9.2x partials are characteristic of bar percussion). Gives
    // a distinctive "decision logged" tone unlike any other sound in the system.
    const notes = [659.25, 880.0]; // E5 → A5 — pleasant ascending interval (perfect 4th)
    const noteDelay = 0.09;
    const duration = 0.55;
    const partials = [1, 4, 9.2];
    const partialAmps = [1, 0.35, 0.12];

    notes.forEach((fundamental, noteIdx) => {
      const startOffset = noteDelay * noteIdx;
      partials.forEach((partial, partIdx) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();

        osc.connect(gain);
        gain.connect(audioContext.destination);

        osc.frequency.value = fundamental * partial;
        osc.type = 'sine';

        // Mallet envelope: fast attack, exponential decay (no sustain)
        const start = audioContext.currentTime + startOffset;
        const peak = volume * partialAmps[partIdx] * 0.45;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(peak, start + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

        osc.start(start);
        osc.stop(start + duration);
      });
    });
  }

  private async generateHarpSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Harp: fast ascending arpeggio over a major 7 chord with long sustain.
    // Distinct from Marimba (which is two bright mallet notes) — the Harp is
    // a rolling 4-note glissando with shimmer, making it feel like "something arrived".
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6 (C major)
    const noteSpacing = 0.05; // 50ms between plucks → quick arpeggio
    const duration = 1.6;

    notes.forEach((freq, idx) => {
      const start = audioContext.currentTime + idx * noteSpacing;

      // Two stacked oscillators per note (sine + soft triangle) for a richer pluck
      const tones: { type: OscillatorType; gain: number }[] = [
        { type: 'sine', gain: 0.55 },
        { type: 'triangle', gain: 0.18 },
      ];

      tones.forEach(t => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();

        osc.connect(gain);
        gain.connect(audioContext.destination);

        osc.frequency.value = freq;
        osc.type = t.type;

        // Plucked-string envelope: instant attack, exponential ring-out
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(volume * t.gain * 0.4, start + 0.003);
        gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

        osc.start(start);
        osc.stop(start + duration);
      });
    });
  }

  private async generateSuccessSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Success: ascending major chord
    const chord = [523.25, 659.25, 783.99]; // C-E-G
    const delays = [0, 0.1, 0.2];
    
    chord.forEach((freq, i) => {
      setTimeout(() => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        
        osc.connect(gain);
        gain.connect(audioContext.destination);
        
        osc.frequency.value = freq;
        osc.type = 'sine';
        
        gain.gain.setValueAtTime(volume * 0.4, audioContext.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        osc.start(audioContext.currentTime);
        osc.stop(audioContext.currentTime + 0.5);
      }, delays[i] * 1000);
    });
  }

  private async generateWarningSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Warning: alternating tones
    const freq1 = 800;
    const freq2 = 600;
    
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        
        osc.connect(gain);
        gain.connect(audioContext.destination);
        
        osc.frequency.value = i % 2 === 0 ? freq1 : freq2;
        osc.type = 'square';
        
        gain.gain.setValueAtTime(volume * 0.5, audioContext.currentTime);
        gain.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.15);
        
        osc.start(audioContext.currentTime);
        osc.stop(audioContext.currentTime + 0.15);
      }, i * 150);
    }
  }

  private async generateErrorSound(audioContext: AudioContext, volume: number): Promise<void> {
    // Error: harsh buzz
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    
    osc.connect(gain);
    gain.connect(audioContext.destination);
    
    osc.frequency.value = 150;
    osc.type = 'sawtooth';
    
    gain.gain.setValueAtTime(volume * 0.7, audioContext.currentTime);
    gain.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.4);
    
    osc.start(audioContext.currentTime);
    osc.stop(audioContext.currentTime + 0.4);
  }

  private mapNotificationTypeToSound(notificationType?: NotificationType): SoundType {
    if (!notificationType) return SoundType.Default;

    switch (notificationType) {
      case NotificationType.TaskAssigned:
      case NotificationType.TaskCompleted:
      case NotificationType.TaskOverdue:
        return SoundType.Task;
      
      case NotificationType.EventInvite:
      case NotificationType.EventReminder:
        return SoundType.Calendar;
      
      case NotificationType.TemplateReview:
      case NotificationType.TemplateCorrections:
      case NotificationType.TemplateApproved:
        return SoundType.Template;
      
      case NotificationType.InventoryUpdate:
      case NotificationType.InventoryAlert:
        return SoundType.Inventory;
      
      case NotificationType.CommissionUpdate:
        return SoundType.Commission;
      
      case NotificationType.ChatMessage:
        return SoundType.Message;
      
      case NotificationType.SystemAlert:
        return SoundType.Warning;
      
      default:
        return SoundType.Default;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Predefined sound sequences for special events
  public async playWelcomeSound(): Promise<void> {
    await this.playSequence([SoundType.Chime, SoundType.Success], 200);
  }

  public async playErrorSequence(): Promise<void> {
    await this.playSequence([SoundType.Error, SoundType.Error], 150);
  }

  public async playSuccessChord(): Promise<void> {
    await this.playChord([523.25, 659.25, 783.99], 0.5); // C major chord
  }

  public async playAlertSequence(): Promise<void> {
    await this.playSequence([SoundType.Warning, SoundType.Warning, SoundType.Warning], 100);
  }

  // Test all sounds (for settings/debugging)
  public async testAllSounds(): Promise<void> {
    const soundTypes = Object.values(SoundType);
    for (const soundType of soundTypes) {
      await this.playSound(soundType);
      await this.delay(300);
    }
  }
}

// Export singleton instance
export const soundNotificationService = SoundNotificationService.getInstance();
